import {
  eachDayOfInterval, min, max, startOfDay, endOfDay,
  addMonths,
  addWeeks,
  addDays,
  addMinutes,
  addMilliseconds,
  subHours,
  subMinutes,
  differenceInMinutes,
  eachMonthOfInterval,
  isEqual,
} from 'date-fns';
import { format as formatTZ, fromZonedTime, toZonedTime } from 'date-fns-tz';
import type { IClientOrder, WeekDay } from '@types';
import { minutesToHM } from '@utils/minutes';
import type { SlotDate } from '@utils/calendar/Cell';
import { formatTimeTemplate } from '@ui/utils/times';
import { is12HourTimeFormat } from '@utils/company';
import { isBetween, isBetweenStrictly } from '@utils/comparisons';

function _formatDate(dateString?: string | Date, options: Intl.DateTimeFormatOptions) {
  if (!dateString) return '';
  const { locale } = useI18n();
  return new Date(dateString).toLocaleString(locale.value, options);
}

const numericDateFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
};

export function formatDay(dateString?: string | Date): string {
  return _formatDate(dateString, numericDateFormatOptions);
}

const calendarDateFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  weekday: 'short',
};

export function formatDateLong(dateString?: string | Date): string {
  return _formatDate(dateString, calendarDateFormatOptions);
}

export function getTimezoneName(timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
  const offset = formatTZ(new Date(), 'O', { timeZone });
  return `${timeZone} (${offset})`
}

function getLocalizedDateTimeFormatter(options: Intl.DateTimeFormatOptions) {
  const { locale } = useI18n();
  return Intl.DateTimeFormat(locale.value, options);
}

export function getWeekdayName(date: Date) {
  const dateTimeFormat = getLocalizedDateTimeFormatter({ weekday: 'short' });
  return dateTimeFormat.format(date);
  // return format(date, 'eeeeee');
}

export function getMonthName(date: Date) {
  const dateTimeFormat = getLocalizedDateTimeFormatter({ month: 'long' });
  return dateTimeFormat.format(date);
  // return format(date, 'MMMM');
}

export enum TimeFormats {
  Time = 'hh:mm',
  Separate = 'h m'
}


function twoCharacterFormat(v: number) {
  return v < 10 ? '0' + v : v;
}

export function minutesToTime(minutes: number, textFormat = TimeFormats.Time) {
  if (minutes === undefined) return;
  const { h, m } = minutesToHM(minutes);
  if (textFormat === TimeFormats.Separate) return `${h} ${'word.hour.letter'} ${m} ${'word.minute.short'.toLowerCase()}`
  return `${twoCharacterFormat(h)}:${twoCharacterFormat(m)}`
}

type WeekDayObject<T> = { [key in WeekDay]: T };

function daysToObject(days: WeekDay[]) {
  return days.reduce((res, day) => {
    res[day] = true;
    return res;
  }, {} as WeekDayObject<boolean>);
}

const WeekDaysPeriods: { values: WeekDay[], text: string }[] = [
  { values: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'], text: 'word.weekday.mo-su' },
  { values: ['Mo', 'Tu', 'We', 'Th', 'Fr'], text: 'word.weekday.mo-fr' },
  { values: ['Sa', 'Su'], text: 'word.weekday.sa-su' },
];

const WeekDaysOptions: WeekDayObject<string> = {
  'Mo': 'word.weekday.short.1',
  'Tu': 'word.weekday.short.2',
  'We': 'word.weekday.short.3',
  'Th': 'word.weekday.short.4',
  'Fr': 'word.weekday.short.5',
  'Sa': 'word.weekday.short.6',
  'Su': 'word.weekday.short.0',
};

function checkOnPeriod(weekDays: WeekDay[]) {
  const daysObject = daysToObject(weekDays);
  return WeekDaysPeriods.find(period => period.values.every(d => daysObject[d]));
}

function getPeriodName(weekDays: WeekDay[]) {
  const period = checkOnPeriod(weekDays);
  if (period) return [period.text];

  return weekDays.map(d => WeekDaysOptions[d]);
}

export function formatWorkingTime({ weekDays, timeStart: start, timeEnd: end }: {
  weekDays: WeekDay[],
  timeStart: number,
  timeEnd: number
}) {
  const period = getPeriodName(weekDays);
  const timeStart = minutesToTime(start)
  const timeEnd = minutesToTime(end)

  return {
    period,
    // timeStart,
    // timeEnd,
    timeText: `${timeStart} — ${timeEnd}`,
  }
}

export function getDaysPeriod(start: Date, end: Date): Date[] {
  return eachDayOfInterval({ start, end })
}

export function getMonthsPeriod(start: Date, end: Date): Date[] {
  return eachMonthOfInterval({ start, end })
}

export function getSlotsStartDate() {
  return startOfDay(subHours(new Date(), 12));
}

export function minDate(days: Date[]): Date {
  return min(days);
}

export function getMaxDate(days: Date[]): Date {
  return max(days);
}

export function is12HourFormat() {
  const { company } = useCompanyStore();
  return is12HourTimeFormat(company);
}

export function formatTime(time: Date) {
  return formatTimeTemplate(time, is12HourFormat());
}

export function formatSlotTime(slot: SlotDate, is12 = is12HourFormat()) {
  return `${formatTimeTemplate(slot.from, is12)} — ${formatTimeTemplate(slot.to, is12)}`;
}

export function formatHM(minutes: number, i18n: ReturnType<typeof useI18n>) {
  const { h, m } = minutesToHM(minutes)
  const hText = h ? `${h} ${i18n.t('word.hour.letter')} ` : '';
  const mText = m ? `${m} ${i18n.t('word.minute.short').toLowerCase()}` : '';
  return (hText + mText) || `0 ${i18n.t('word.minute.short').toLowerCase()}`;
}

export function useTimeZone<T extends Date | string>(date: T, timezone?: string) {
  return timezone ? toZonedTime(date, timezone) : date;
}

export function teleportDateToTz(date: Date, timezone?: string) {
  return fromZonedTime(date, timezone);
}

export function teleportSlotToTz(slot: SlotDate, timezone?: string) {
  return { from: teleportDateToTz(slot.from, timezone), to: teleportDateToTz(slot.to, timezone) };
}

export function isSameDay(from: Date, to: Date) {
  /** 00:00 относится к прошлому дню */
  const almostTo = subMinutes(to, 1);
  return from.getFullYear() === almostTo.getFullYear()
    && from.getMonth() === almostTo.getMonth()
    && from.getDate() === almostTo.getDate();
}

export function clearDate(date: Date) {
  const _date = new Date(date)
  return new Date(_date.getFullYear(), _date.getMonth(), _date.getDate())
}

export function timezoneAdaptation(order: IClientOrder): IClientOrder {
  const isOffline = !order.address?.isOnline;
  const timeZone = order.address?.timezone || '';

  if (isOffline && timeZone) {
    order.dateTimeStart = order.dateTimeStart && fromZonedTime(order.dateTimeStart, timeZone);
    order.dateTimeEnd = order.dateTimeEnd && fromZonedTime(order.dateTimeEnd, timeZone);
  } else {
    order.dateTimeStart = order.dateTimeStart && fromZonedTime(order.dateTimeStart, timeZone);
    order.dateTimeEnd = order.dateTimeEnd && fromZonedTime(order.dateTimeEnd, timeZone);
  }

  return order;
}

export {
  addMonths,
  addWeeks,
  addDays,
  addMinutes,
  addMilliseconds,
  subMinutes,
  startOfDay,
  endOfDay,
  differenceInMinutes,
  differenceInMinutes as durationDifference,
  isEqual as isDatesEqual,
  formatDate,
  isBetween,
  isBetweenStrictly,
};
